import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { ApiFailure, ApiResult } from '../api.js';

export type RequestStatus = 'loading' | 'ready' | 'error';

export interface RequestState<T> {
  status: RequestStatus;
  data: T | null;
  error: ApiFailure | null;
  /** Re-runs the load. Data already shown stays on screen while it reloads (no flash to skeleton). */
  reload: () => void;
  /** For optimistic updates after a mutation; pass a function to derive from the current value. */
  setData: Dispatch<SetStateAction<T | null>>;
  /** True while a reload is in flight over existing data. */
  refreshing: boolean;
}

/** What `useRequest` answers: the state, and whether its data answers the deps of this render. */
export interface KeyedRequestState<T> extends RequestState<T> {
  /**
   * Whether `data` answers the current `deps` — false while a request for new deps runs over the previous answer, and
   * once it has failed. Why (the Phase 2a review of the Costs line, D29/G8): a caller that labels held data with its live
   * inputs — a filter, a period — says what the figures never counted unless it asks this first.
   */
  current: boolean;
}

/** Whether two dependency lists hold the same values, as React compares an effect's. */
export function sameDeps(a: readonly unknown[] | null, b: readonly unknown[]): boolean {
  return a !== null && a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}

/**
 * Loads data with cancellation on unmount and on dependency change. The first load is `loading`;
 * later reloads keep the previous data and set `refreshing`, so a table never collapses to a
 * skeleton on a background refresh (03-components §1).
 */
export function useRequest<T>(load: (signal: AbortSignal) => Promise<ApiResult<T>>, deps: readonly unknown[]): KeyedRequestState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [status, setStatus] = useState<RequestStatus>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  // The deps the held data answered, set with it — so `current` is exact in every render, the one between a change of
  // deps and its request's start included.
  const [answered, setAnswered] = useState<readonly unknown[] | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const hasDataRef = useRef(false);

  useEffect(() => {
    const asked = deps;
    const controller = new AbortController();
    if (hasDataRef.current) setRefreshing(true);
    else setStatus('loading');
    void loadRef.current(controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.ok) {
        hasDataRef.current = true;
        setData(result.data);
        setAnswered(asked);
        setError(null);
        setStatus('ready');
      } else if (!result.aborted) {
        setError(result);
        setStatus('error');
      }
      setRefreshing(false);
    });
    return () => controller.abort();
    // Why: `deps` is the caller's cache key by design; `load` is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { status, data, error, reload, setData, refreshing, current: data !== null && sameDeps(answered, deps) };
}
